import { resolveCmsDevProxyUrl } from './cms-dev-proxy-url';
import {
  hasAudioPlaybackPermission,
  waitForAudioPlaybackPermission,
} from './audio-playback-permission';

export let audioManager: AudioManager | null = null;

type AudioSource = {
  audio: Audio;
  loadedUrl: string | null;
  playbackGeneration: number;
  pendingEndWait: { listener: () => void; resolve: () => void } | null;
}

type PlaybackFailure = {
  requestedUrl: string;
  resolvedUrl: string;
  error: unknown;
}

const MEDIA_ERROR_LABELS: Readonly<Record<number, string>> = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
};

function describeMediaError(mediaError: MediaError | null): string | null {
  if (!mediaError) return null;
  const label = MEDIA_ERROR_LABELS[mediaError.code] ?? 'UNKNOWN';
  return `${label} (${mediaError.code}) ${mediaError.message}`.trim();
}

export class AudioManager {
  audioSources = new Map<string, AudioSource>();

  constructor() {
    audioManager = this;
    this.audioSources.set('scene', {
      audio: new Audio(),
      loadedUrl: null,
      playbackGeneration: 0,
      pendingEndWait: null,
    });
    this.audioSources.set('audioplayer', {
      audio: new Audio(),
      loadedUrl: null,
      playbackGeneration: 0,
      pendingEndWait: null,
    });
  }

  async play(url: string, audioSourceName: 'scene' | 'audioplayer', continuePlaybackIfSameURL: boolean = false) {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;

    const playbackUrl = url.trim();
    if (!playbackUrl) {
      console.warn('Audio playback skipped: missing url');
      return;
    }

    const generation = audioSource.playbackGeneration;

    if (!hasAudioPlaybackPermission()) {
      await waitForAudioPlaybackPermission();
      if (generation !== audioSource.playbackGeneration) return;
    }

    this.cancelPendingEndWait(audioSourceName);

    const resolvedUrl = resolveCmsDevProxyUrl(playbackUrl) ?? playbackUrl;
    const shouldReload = !continuePlaybackIfSameURL || audioSource.loadedUrl !== resolvedUrl;
    if (shouldReload) {
      this.loadSource(audioSource, resolvedUrl);
    }

    try {
      await audioSource.audio.play();
    } catch (error) {
      if (generation !== audioSource.playbackGeneration) return;
      this.warnPlaybackFailure(audioSource, {
        requestedUrl: playbackUrl,
        resolvedUrl,
        error,
      });
      return;
    }

    if (generation !== audioSource.playbackGeneration) {
      audioSource.audio.pause();
    }
  }

  private loadSource(audioSource: AudioSource, resolvedUrl: string) {
    audioSource.audio.pause();
    audioSource.audio.src = resolvedUrl;
    audioSource.loadedUrl = resolvedUrl;
  }

  private warnPlaybackFailure(audioSource: AudioSource, failure: PlaybackFailure) {
    const errorName = failure.error instanceof Error ? failure.error.name : 'Error';
    const errorMessage = failure.error instanceof Error ? failure.error.message : String(failure.error);
    const mediaError = describeMediaError(audioSource.audio.error);
    console.warn(
      `Audio playback blocked or failed: ${errorName}: ${errorMessage} | requestedUrl=${failure.requestedUrl} | resolvedUrl=${failure.resolvedUrl}`,
      {
        requestedUrl: failure.requestedUrl,
        resolvedUrl: failure.resolvedUrl,
        currentSrc: audioSource.audio.currentSrc || null,
        mediaError,
        networkState: audioSource.audio.networkState,
        readyState: audioSource.audio.readyState,
      },
    );
  }

  waitUntilPlaybackEnded(audioSourceName: 'scene' | 'audioplayer'): Promise<void> {
    const audioSource = this.audioSources.get(audioSourceName);
    if (!audioSource) return Promise.resolve();
    if (audioSource.audio.ended) return Promise.resolve();

    this.cancelPendingEndWait(audioSourceName);

    return new Promise((resolve) => {
      const listener = () => {
        audioSource.pendingEndWait = null;
        resolve();
      };
      audioSource.pendingEndWait = { listener, resolve: listener };
      audioSource.audio.addEventListener('ended', listener, { once: true });
    });
  }

  pause(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.pause();
  }

  stop(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.pause();
    this.cancelPendingEndWait(audioSourceName);
    audioSource.audio.pause();
    try {
      audioSource.audio.currentTime = 0;
    } catch {
      /* some browsers throw when resetting before metadata */
    }
    audioSource.audio.removeAttribute('src');
    audioSource.audio.load();
    audioSource.loadedUrl = null;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('spotar:audioStopped'));
    }
  }

  private cancelPendingEndWait(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName);
    const pending = audioSource?.pendingEndWait;
    if (!pending) return;
    audioSource!.pendingEndWait = null;
    audioSource!.audio.removeEventListener('ended', pending.listener);
    pending.resolve();
  }

  isPaused(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.paused;
  }

  isPlaying(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return !audioSource.audio.paused && !this.isEnded(audioSourceName);
  }

  isEnded(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.ended;
  }

  addAudioEventListener(
    audioSourceName: 'scene' | 'audioplayer',
    event: keyof HTMLMediaElementEventMap,
    callback: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ) {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.addEventListener(event, callback, options);
  }

  removeAudioEventListener(
    audioSourceName: 'scene' | 'audioplayer',
    event: keyof HTMLMediaElementEventMap,
    callback: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions
  ) {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    audioSource.audio.removeEventListener(event, callback, options);
  }

  getAudioDuration(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.duration;
  }

  getReadyState(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.readyState;
  }

  getCurrentTime(audioSourceName: 'scene' | 'audioplayer') {
    const audioSource = this.audioSources.get(audioSourceName)
    if (!audioSource) return;
    return audioSource.audio.currentTime;
  }
}
